import { API } from '~apis/axios/instance';
import { LabeledBankAccountTransaction } from '~apis/cash-flow-overview/transactions/getTransactions';
import {
  GroupRuleOperator,
  RULE_FILED_TYPE,
  RULE_TYPE_FOR_BANK,
  RULE_TYPE_FOR_TAX_INVOICE,
  RuleCashFlowType,
  RuleOperator,
} from '~models/rule';
import { Page, ResponseBaseWithData } from '~types/response';

export interface RuleItem<T> {
  id: number;
  order: number;
  cashFlowType: RuleCashFlowType;
  ruleName: string;
  categoryLabelKey: string;
  category: string;
  businessEntityId: number;
  businessEntityLabelName: string;
  customLabelId: number;
  customLabelName: string;
  ruleGroup: IRuleGroup<T>;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  isEnable: boolean;
  companyDetailedCategory: string;
  isActive?: boolean;
}

export interface IRuleGroup<T> {
  groupOperator: GroupRuleOperator;
  ruleItems: IRuleItemsItem<T>[];
  subGroup: IRuleGroup<T>[];
}

interface IRuleItemsItem<T> {
  field: T;
  value: string;
  operator: RuleOperator;
  isNot: boolean;
  isEnable: boolean;
}

export interface IRuleGroupForTaxInvoice {
  groupOperator: GroupRuleOperator;
  ruleItems: IRuleItemsItemForTaxInvoice[];
  subGroup: IRuleGroupForTaxInvoice[];
}

interface IRuleItemsItemForTaxInvoice {
  field: RULE_TYPE_FOR_TAX_INVOICE;
  value: string;
  operator: RuleOperator;
  isNot: boolean;
  isEnable: boolean;
}

export const getRuleList = (companyId: string) => {
  return API.get<ResponseBaseWithData<RuleItem<RULE_TYPE_FOR_BANK>[]>>(
    `/api/v3/companies/${companyId}/labeling-rules`,
  );
};

export const getRuleListItem = (companyId: string, ruleId: number | null) => {
  return API.get<ResponseBaseWithData<RuleItem<RULE_TYPE_FOR_BANK>>>(
    `/api/v3/companies/${companyId}/labeling-rules/${ruleId}`,
  );
};

export interface RuleRequest {
  cashFlowType: RuleCashFlowType;
  ruleName: string;
  categoryLabelKey: string | null;
  businessEntityId: number | null;
  customLabelId: number | null;
  ruleGroup: IRuleGroup<RULE_FILED_TYPE>;
  overwrite: boolean;
}

export const addRule = ({ companyId, request }: { companyId: string; request: RuleRequest }) => {
  return API.post<ResponseBaseWithData>(`/api/v3/companies/${companyId}/labeling-rules`, {
    data: request,
  });
};

export const updateRule = ({
  companyId,
  ruleId,
  request,
}: {
  companyId: string;
  ruleId: number;
  request: RuleRequest & {
    isEnable: boolean;
  };
}) => {
  return API.patch<ResponseBaseWithData>(
    `/api/v3/companies/${companyId}/labeling-rules/${ruleId}`,
    {
      data: request,
    },
  );
};

export const updateRuleEnable = ({
  companyId,
  ruleId,
  isEnable,
}: {
  companyId: string;
  ruleId: number;
  isEnable: boolean;
}) => {
  return API.patch<ResponseBaseWithData>(
    `/api/v3/companies/${companyId}/labeling-rules/${ruleId}/enabled`,
    {
      data: { isEnable },
    },
  );
};

export const deleteLabelingRule = ({
  companyId,
  ruleId,
}: {
  companyId: string;
  ruleId: number;
}) => {
  return API.delete<ResponseBaseWithData>(
    `/api/v2/companies/${companyId}/labeling-rules/${ruleId}`,
  );
};

export const updateRulePriority = ({
  companyId,
  labelingRuleId,
  priority,
}: {
  companyId: string;
  labelingRuleId: number;
  priority: number;
}) => {
  return API.post<ResponseBaseWithData>(
    `/api/v3/companies/${companyId}/labeling-rules/${labelingRuleId}/priority`,
    {
      data: { priority },
    },
  );
};

export const simulateRule = ({
  companyId,
  request,
  page,
  size,
}: {
  companyId: string;
  request: Omit<RuleRequest, 'ruleName' | 'overwrite'> & {
    ruleId: number | null;
  };
  page: number;
  size: number;
}) => {
  return API.post<
    ResponseBaseWithData<
      Page<LabeledBankAccountTransaction> & {
        conflicts: LabeledBankAccountTransaction[];
      }
    >
  >(`/api/v3/companies/${companyId}/labeling-rules/transactions/flat`, {
    data: { ...request, page, size },
  });
};

export interface RuleRecommendation {
  companyId: string;
  ruleGroup: IRuleGroup<RULE_FILED_TYPE>;
  count: number;
}

export const getRuleRecommendationList = (companyId: string) => {
  return API.get<
    ResponseBaseWithData<{
      items: RuleRecommendation[];
    }>
  >(`/api/v3/companies/${companyId}/labeling-rules/recommendations`);
};

export const postDenyRuleRecommendation = ({
  companyId,
  request,
}: {
  companyId: string;
  request: RuleRecommendation;
}) => {
  return API.post<ResponseBaseWithData>(
    `/api/v3/companies/${companyId}/labeling-rules/recommendations/deny`,
    {
      data: {
        recommendation: request,
      },
    },
  );
};
